/* eslint-disable indent */
/* eslint-disable array-bracket-newline */
import * as turf from '@turf/turf';
import { getDeviceId } from '@amplitude/analytics-browser';
import lookup from 'country-code-lookup';
import track from 'utils/amplitudeWrapper';
import { Base64 } from 'js-base64';
import ButtonBase, { EButtonType } from 'components/Buttons/ButtonBase';
import { useAppState } from 'context/AppState';
import { useRecommendationFormActions } from 'context/actions/recommendationFormActions';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useBreakpoint } from 'hooks';
import { Skeleton } from 'syngenta-digital-cropwise-react-ui-kit';
import { getBagsCount } from 'utils/getBagsCount';
import { generateEcommerceUrl } from 'utils/generateEcommerceUrl';
import { generateUkraineStoreUrl, UkraineHybridInput } from 'utils/generateUkraineStoreUrl';
import { CropConstants } from 'utils/constants/Crop';
import {
  isPoland,
  isSouthAfrica,
  isUkraine,
  isUK,
  isFrance,
  isGermany,
  isSerbia,
  isBelgium,
  isNetherlands,
  isBrazil,
} from 'utils/countryCode';
import {
  StickyBar,
  StyledSpinner,
  SkeletonStyled,
  SkeletonMobileContainer,
  SkeletonMobileTopContainer,
} from './ActionBar.styles';
import { SeedDensityField } from 'pages/RecommendationV2/Sections/SeedDensityAdvice';
import Download from 'assets/icons/icon-download.svg';
import Voucher from 'assets/icons/icon-voucher.svg';
import BuyNow from 'assets/icons/buy-now.svg';
import { getEmailId } from 'utils/contactEmail';
import ProxyLayer from 'base/api/ProxyLayer';
import { PreBookSeedsPayload } from 'base/types/PreBookSeeds';
import { updatePhoneNumberBasedOnCountry } from 'utils/phoneNumberFormatter';
import { isScriptGenerated } from 'utils/isScriptGenerated';
import useSeedsOnboarding from 'hooks/useSeedsOnboarding';

// Ukraine store URLs mapping
const UKRAINE_STORE_URLS = {
  CORN: 'https://store.syngenta.ua/kukurudza',
  SUNFLOWER: 'https://store.syngenta.ua/sonyashnuk',
} as const;
const POLAND_STORE_URLS = 'https://www.syngenta.pl/gdzie-kupic';

interface ActionBarProps {
  isLoading?: boolean;
  cropId?: string | number;
  pageLoadStartTime: number;
  showDownloadBtn?: boolean;
}
interface contactAttribute {
  firstName: string;
  lastName: string;
}

interface ButtonsComponentProps {
  country: string;
  downloadPdf: () => void | Promise<void>;
  t: TFunction;
  voucherParams?: string;
  showDownloadBtn?: boolean;
  isDDG?: boolean;
  crop_id?: string | number;
  onPreBookSeeds?: () => void;
  isPreBooking?: boolean;
  isSIPUser?: boolean;
  isScriptGenerated?: boolean;
  ukraineStoreUrl?: string;
  isSeedsOnboarding?: boolean;
}

const ActionBarButtons = ({
  country,
  downloadPdf,
  t,
  voucherParams,
  showDownloadBtn = true,
  isDDG,
  crop_id,
  onPreBookSeeds,
  isPreBooking = false,
  isSIPUser = false,
  isScriptGenerated = false,
  ukraineStoreUrl = '',
  isSeedsOnboarding = false,
}: ButtonsComponentProps) => {
  const { isMobile } = useBreakpoint();
  const deviceId = getDeviceId();

  const tempHideDownloadPdf = false;

  const handleBuyClick = () => track('process', { 'commercial process': 'e-commerce' });
  const handleEVoucherClick = () => track('process', { 'commercial process': 'e-voucher' });
  const primaryBtnWidth = () => {
    if (isMobile) {
      if (!tempHideDownloadPdf) {
        return '100%';
      }
      if (showDownloadBtn) {
        if (!isDDG) {
          return '';
        }
        return '48%';
      }
      return '100%';
    }
    return '';
  };

  const showPreBookSeedsBtn = () => {
    // Hide pre-book seeds button if script-generated and country is Germany
    if (isScriptGenerated && isGermany(country)) {
      return false;
    }
    return isDDG && !isScriptGenerated;
  };

  const getUkraineStoreUrl = (cropId: string | number): string => {
    const cornCropIds = [
      CropConstants.CORN_CROP_ID,
      CropConstants.SILAGE_CORN_CROP_ID,
      CropConstants.SUMMER_CORN_CROP_ID,
    ];
    return cornCropIds.includes(Number(cropId))
      ? UKRAINE_STORE_URLS.CORN
      : UKRAINE_STORE_URLS.SUNFLOWER;
  };

  const renderBuyNowButton = (href: string) => (
    <a
      href={isSeedsOnboarding ? undefined : href}
      target="_blank"
      rel="noreferrer"
      style={{ width: primaryBtnWidth() }}
      onClick={(e) => isSeedsOnboarding && e.preventDefault()}
    >
      <ButtonBase
        className="headerBtnWrap"
        text={t('Buy Now')}
        textSize="MEDIUM"
        type={EButtonType.primary}
        fullWidth={isMobile}
        onClick={handleBuyClick}
        style={{ height: '44px' }}
        iconAlign="LEFT"
        icon={<img src={BuyNow} alt="No img found" style={{ width: '20px', height: '20px' }} />}
        isDisabled={isSeedsOnboarding}
      />
    </a>
  );

  if (showPreBookSeedsBtn() && isUkraine(country)) {
    return renderBuyNowButton(ukraineStoreUrl || getUkraineStoreUrl(crop_id || CropConstants.SUNFLOWER_CROP_ID));
  }

  if (showPreBookSeedsBtn() && isPoland(country)) {
    // remove this comment once generateEcommerceUrl is ready
    // const url = generateEcommerceUrl();
    const url = POLAND_STORE_URLS;
    return renderBuyNowButton(`${url}/${voucherParams}?deviceId=${deviceId}`);
  }

  // Regular pre-book seeds logic for other countries
  if (showPreBookSeedsBtn()) {
    return (
      <>
        {showPreBookSeedsBtn() &&
          !isUK(country) &&
          !isFrance(country) &&
          !isBelgium(country) &&
          !isBrazil(country) && (
            <>
              {/* Render different button behavior for SIP users vs regular users */}
              {isSerbia(country) || isSIPUser ? (
                // For SIP users, use button without anchor tag to handle SIP integration
                <div
                  style={{
                    width: primaryBtnWidth(),
                    position: 'relative',
                  }}
                >
                  <ButtonBase
                    textSize="MEDIUM"
                    className="headerBtnWrap"
                    text={isPreBooking ? t('Processing...') : t('Pre-book Seeds')}
                    type={EButtonType.primary}
                    fullWidth={isMobile}
                    onClick={() => {
                      handleEVoucherClick();
                      onPreBookSeeds?.();
                    }}
                    style={{ height: '44px' }}
                    iconAlign="LEFT"
                    icon={
                      isPreBooking ? (
                        <StyledSpinner />
                      ) : (
                        <img
                          src={Voucher}
                          alt="No img found"
                          style={{ width: '20px', height: '20px' }}
                        />
                      )
                    }
                    isDisabled={isPreBooking || isSeedsOnboarding}
                  />
                </div>
              ) : (
                // For regular users, use anchor tag to redirect to voucher URL
                <a
                  href={
                    isSeedsOnboarding
                      ? undefined
                      : isSouthAfrica(country)
                      ? getEmailId(CropConstants.SOUTH_AFRICA_CODE)
                      : `${process.env.REACT_APP_VOUCHER_URL}?param=${voucherParams}&deviceId=${deviceId}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    width: primaryBtnWidth(),
                  }}
                  onClick={(e) => isSeedsOnboarding && e.preventDefault()}
                >
                  <ButtonBase
                    textSize="MEDIUM"
                    className="headerBtnWrap"
                    text={isSouthAfrica(country) ? t('Contact Seed Sales') : t('Pre-book Seeds')}
                    type={EButtonType.primary}
                    fullWidth={isMobile}
                    onClick={() => {
                      handleEVoucherClick();
                    }}
                    style={{ height: '44px' }}
                    iconAlign="LEFT"
                    icon={
                      !isSouthAfrica(country) && (
                        <img
                          src={Voucher}
                          alt="No img found"
                          style={{ width: '20px', height: '20px' }}
                        />
                      )
                    }
                    isDisabled={isSeedsOnboarding}
                  />
                </a>
              )}
            </>
          )}
      </>
    );
  }

  // Return null if no conditions match
  return null;
};

export default function ActionBar(props: ActionBarProps) {
  const { isLoading, cropId, pageLoadStartTime, showDownloadBtn = true } = props;
  const [btnDisplayDelay, setBtnDisplayDelay] = useState(true);
  const [gettingPDF, setGettingPDF] = useState(false);
  const [isPreBooking, setIsPreBooking] = useState(false);
  const [ukraineStoreUrl, setUkraineStoreUrl] = useState('');
  const { setVoucherParams } = useRecommendationFormActions();

  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const {
    apiData: { recommendation, recommendationCropwise, productCatalog, countryList },
    recommendationForm: { voucherParams, recommendationSeedsInfo },
    flow: { sfdcCampaignId },
  } = useAppState();
  const [country] = useState(recommendationCropwise?.country);
  const { userContactFromAudit } = recommendation || {};
  const { POLAND, CORN_SMALLCASE, GRAIN_CORN } = CropConstants;
  const isDDG = recommendationCropwise?.source === 'DDG';
  const countryCode = recommendationCropwise?.country || '';
  const scriptGenerated = isScriptGenerated(recommendationCropwise);
  const isSeedsOnboarding = useSeedsOnboarding();

  // Determine if StickyBar should render
  const shouldRenderStickyBar =
    isDDG && (isPoland(countryCode) || isUkraine(countryCode) || !isUK(countryCode));

  useEffect(() => {
    if (!countryList.length) {
      return;
    }
    // Generate voucher params
    if (recommendationCropwise && productCatalog.length) {
      const [
        {
          multi_field: {
            config: {
              recommendation: recommendationResults,
              agronomicInputs: recommendationAgronomicInputs,
            },
            geometry,
          },
        },
      ] = recommendationCropwise.recommendations;
      const { id: recommendationID } = recommendationCropwise;
      const [properties] = geometry.properties || [];
      const plots = properties?.fields.map((field) => {
        // Defensive safeguards in case turf.centroid or field.geometry is undefined in tests
        let longitude = 0;
        let latitude = 0;
        try {
          if (field?.geometry && (turf as any)?.centroid) {
            const centroid = (turf as any).centroid(field.geometry);
            const coords = centroid?.geometry?.coordinates;
            if (Array.isArray(coords) && coords.length >= 2) {
              [longitude, latitude] = coords;
            }
          }
        } catch (e) {
          // Fallback already set to 0,0 – swallow error to avoid breaking voucher generation
          console.error('centroid calc failed', e);
        }
        return {
          name: field.name,
          latitude,
          longitude,
          acreage: Number(field.declared_area.toFixed(2)),
          acreageUnitOfMeasure: 'ha',
        };
      });
      const recommendedHybrids: { name: string; quantity: number }[] = recommendationResults
        .map((item) => {
          const field = properties?.fields.find((fieldItem) => fieldItem.id === item.FieldId);
          if (item?.SeedRankings?.length) {
            const seedName = item.SeedRankings[0].Seed?.SeedName;
            const countryCode = lookup.byCountry(recommendationCropwise.country)?.iso3;
            const product = productCatalog.find((p) => p.productName === seedName);
            const defaultSeedingRate = Number(product?.defaultSeedingRate) || 0;
            const hectares = field?.declared_area || 0;
            const spatialClass = recommendationAgronomicInputs?.spatialClass ?? '';
            const bags = getBagsCount({
              product,
              hectares,
              defaultSeedingRate,
              countryList,
              countryCode,
              cropId,
              spatialClass,
            });

            return {
              name: seedName,
              quantity: bags,
            };
          } else {
            return {
              name: 'N/A',
              quantity: 0,
            };
          }
        })
        .filter((item) => item.name && item.name !== 'N/A')
        .reduce((acc: any, current) => {
          // Sum same products/seeds
          const index = acc.findIndex((i: any) => i.name === current.name);
          if (index < 0) {
            acc.push(current);
          } else {
            acc[index].quantity += current.quantity;
          }

          return acc;
        }, []);

      const recoHybridsNames = recommendedHybrids.map((item) => item.name);

      const getSeedName = (rs: any) => {
        if (rs.SeedName) {
          return rs.SeedName;
        }
        return rs.Seed ? rs.Seed.SeedName : '';
      };

      const nextBestHybrids = recommendationResults
        .map((item) => item.SeedRankings.map((rs, index) => index < 6 && (getSeedName(rs) || '')))
        .flat()
        .filter((seed) => seed)
        .filter((seed) => !recoHybridsNames.includes(seed))
        .filter((value, index, self) => self.indexOf(value) === index)
        .slice(0, 2);

      const updatedPhone = userContactFromAudit
        ? userContactFromAudit?.phoneNumber
        : recommendation?.phoneNumber;
      const phoneNumberBasedOnCountry =
        recommendationCropwise.country.toLowerCase().replace(/\s+/g, '') === POLAND.toLowerCase()
          ? updatedPhone?.slice(2)
          : updatedPhone;
      const countryCode = getCountryCode(recommendationCropwise.country);
      const firstName = getCapitalName(
        userContactFromAudit ? userContactFromAudit?.firstName : recommendation?.firstName
      );
      const lastName = getCapitalName(
        userContactFromAudit ? userContactFromAudit?.lastName : recommendation?.lastName
      );

      const buildAndSetVoucherParams = () => {
        const SFDCCampaignID = sfdcCampaignId || '';

        const data = {
          recommendationID,
          Country: countryCode,
          cropName:
            recommendationCropwise.recommendations[0].crop_name === CORN_SMALLCASE
              ? GRAIN_CORN
              : recommendationCropwise.recommendations[0].crop_name,
          User: {
            Email: recommendation?.email,
            FirstName: firstName,
            LastName: lastName,
            phone: updatePhoneNumberBasedOnCountry(
              phoneNumberBasedOnCountry,
              recommendationCropwise.country
            ),
          },
          plots,
          recommendedHybrids,
          nextBestHybrids,
          SFDCCampaignID,
          BU: 'SE',
        };

        const safeDataPayload = Base64.encode(JSON.stringify(data));
        setVoucherParams({ voucherParams: safeDataPayload });
      };

      buildAndSetVoucherParams();

      // Generate Ukraine seed-selector URL from hybrid mapping.
      // Uses getUkraineHybrids which reads top 3 seeds per field (seedDemand[0..2])
      // and maps them to light: 1|2|3 (rank). Same product across fields gets qtt summed.
      // sku.sfdcProductCode is used to build the mapping hybrid_name with treatment suffix.
      if (isUkraine(recommendationCropwise.country) && recommendationSeedsInfo?.length) {
        const apiProducts = recommendationCropwise.recommendations[0]?.products ?? [];
        const ukraineHybrids = getUkraineHybrids(recommendationSeedsInfo, apiProducts);
        const cornCropIds = [
          CropConstants.CORN_CROP_ID,
          CropConstants.SILAGE_CORN_CROP_ID,
          CropConstants.SUMMER_CORN_CROP_ID,
        ];
        const fallbackUrl = cornCropIds.includes(Number(cropId))
          ? UKRAINE_STORE_URLS.CORN
          : UKRAINE_STORE_URLS.SUNFLOWER;
        setUkraineStoreUrl(generateUkraineStoreUrl(ukraineHybrids, fallbackUrl));
      }
    }
  }, [
    recommendation,
    recommendationCropwise,
    productCatalog,
    setVoucherParams,
    recommendationSeedsInfo,
    countryList,
    cropId,
    sfdcCampaignId,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isLoading) {
        setBtnDisplayDelay(false);
      }
    }, 3500);
    return () => clearTimeout(timer);
  }, [isLoading]);

  const SkeletonLoader = useMemo(() => {
    if (isMobile) {
      return (
        <SkeletonMobileContainer>
          <SkeletonMobileTopContainer>
            <SkeletonStyled active size="large" />
            {showDownloadBtn && <SkeletonStyled active size="large" />}
          </SkeletonMobileTopContainer>
        </SkeletonMobileContainer>
      );
    }
    return (
      <>
        <Skeleton.Button active size="large" />
        {showDownloadBtn && <Skeleton.Button active size="large" />}
      </>
    );
  }, [isMobile]);

  // const getFileName = (contact: contactAttribute) => {
  //   let name = 'Syngenta-Recommendation';
  //   const countryListWithGrowerName = ['ro', 'hu'];
  //   const countryCode = recommendationCropwise?.country
  //     ? lookup.byCountry(recommendationCropwise?.country?.trim())?.iso2.toLocaleLowerCase()
  //     : '';
  //   if (countryCode) {
  //     if (countryListWithGrowerName.includes(countryCode)) {
  //       const growerName = `${contact?.firstName || ''} ${contact?.lastName || ''}`;
  //       name = `
  //       ${
  //         pdfNamePerCountry.find((con) => con.countryCode === countryCode)?.text ?? ''
  //       }${growerName}`;
  //     } else {
  //       name = pdfNamePerCountry.find((con) => con.countryCode === countryCode)?.text ?? '';
  //     }
  //   }
  //   return name;
  // };

  // const getPreparedFor = (contact: any) => ` ${contact.firstName} ${contact.lastName}`;
  // const PDFPayloadGenerator = usePDFPayloadGenerator({
  //   fileName: getFileName(userContactFromAudit?.firstName ? userContactFromAudit : userContact),
  //   preparedFor: getPreparedFor(
  //     userContactFromAudit?.firstName ? userContactFromAudit : userContact
  //   ),
  //   isLoading,
  // });

  // TODO: Need to handle downloadPdf functionality when Recommendation Page is ready.
  const downloadPdf = async () => {
    //   setGettingPDF(true);
    //   track('download PDF', { 'PDF downloaded': true });
    //   const downloadPDFClickedAt = Date.now();
    //   const downloadPDFClickedTimeDuration = (downloadPDFClickedAt - pageLoadStartTime) / 1000;
    //   track('time taken to download pdf', {
    //     'time taken by user to download pdf': downloadPDFClickedTimeDuration,
    //   });
    //   try {
    //     const payload = await PDFPayloadGenerator();
    //     const pdfData = await ProxyLayer.renderPDF(payload);
    //     setGettingPDF(false);
    //     const url = window.URL.createObjectURL(pdfData);
    //     const link = document.createElement('a');
    //     link.href = url;
    //     link.setAttribute('target', '_blank');
    //     link.setAttribute('download', `${payload.title}.pdf`);
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    //   } catch (e) {
    //     console.error('Error!!', e);
    //     setGettingPDF(false);
    //   }
  };

  const getCountryCode = (prevCountryCode: string) => {
    const countryCode: string | null = prevCountryCode.trim();
    return lookup.byCountry(countryCode)?.iso2;
  };

  const getCapitalName = (name: string | undefined) => {
    return name ? name?.charAt(0).toUpperCase() + name?.slice(1) : '';
  };

  // Builds rank-aware hybrid list for the Ukraine seed-selector URL.
  // Reads all 3 recommendation slots (seedDemand[0..2]) per field.
  // Each slot maps to light: 1 | 2 | 3 (strongly recommended → third choice).
  // Uses sku.sfdcProductCode from the API products to match the Ukraine e-shop mapping
  // (hybrid_name includes the treatment suffix, e.g. SY_ARTOS_FORS_ZEA_VAIBRANS).
  // If the same product appears at rank 1 in multiple fields, quantities are summed
  // and light is preserved as 1 (Option A — all top seeds equally strongly recommended).
  const getUkraineHybrids = (
    seedDensity: SeedDensityField[],
    apiProducts: { product_name: string; sku?: { sfdcProductCode?: string }; quantity?: number; isAlternateProduct?: boolean }[]
  ): UkraineHybridInput[] => {
    // Build a lookup: product_name → sku.sfdcProductCode from the API response
    const skuMap = new Map<string, string>();
    apiProducts.forEach((p) => {
      if (p.product_name && p.sku?.sfdcProductCode) {
        skuMap.set(p.product_name, p.sku.sfdcProductCode);
      }
    });

    const result: UkraineHybridInput[] = [];
    seedDensity.forEach((property: SeedDensityField) => {
      property.seedDemand.slice(0, 3).forEach((demand: any, index: number) => {
        const name: string = demand.Name ?? demand.SeedName;
        const quantity: number = demand.numberOfBags ?? demand.bags ?? 0;
        const light = (index + 1) as 1 | 2 | 3;
        const sfdcProductCode = skuMap.get(name) ?? '';
        if (!name || quantity <= 0) {
          return;
        }
        const existing = result.findIndex((r) => r.name === name);
        if (existing === -1) {
          result.push({ name, sfdcProductCode, quantity, light });
        } else {
          result[existing].quantity += quantity;
          // Keep the better (lower) rank if the same product appears at different ranks
          if (light < result[existing].light) {
            result[existing].light = light;
          }
        }
      });
    });

    // Supplement with products from the API response that were not in seedDemand.
    // This covers alternative products (isAlternateProduct) that may be missing
    // from seedDemand due to UI slicing (e.g. multi-field scenarios).
    apiProducts.slice(0, 3).forEach((p, index) => {
      const name = p.product_name;
      const sfdcProductCode = p.sku?.sfdcProductCode ?? '';
      const quantity = p.quantity ?? 0;
      const light = (index + 1) as 1 | 2 | 3;
      if (!name || !sfdcProductCode || quantity <= 0) {
        return;
      }
      const alreadyIncluded = result.some((r) => r.name === name);
      if (!alreadyIncluded) {
        result.push({ name, sfdcProductCode, quantity, light });
      }
    });

    return result;
  };

  // Get extended attributes for SIP user authentication
  const extendedAttributes =
    recommendationCropwise?.recommendations?.[0]?.multi_field?.config?.agronomicInputs
      ?.extended_attributes;

  const handleSIPIntegration = async () => {
    const currentCountry = recommendationCropwise?.country || '';
    const isSIPUser = extendedAttributes?.isSIPUser || false;

    if (!recommendationCropwise || !isSerbia(currentCountry)) {
      return;
    }
    setIsPreBooking(true);

    try {
      console.log('Starting SIP integration for user:', {
        country: currentCountry,
        isSIPUser,
        recommendationId: recommendationCropwise?.id,
      });

      // Extract data for the payload
      const { recommendations } = recommendationCropwise;
      const {
        multi_field: {
          config: {
            recommendation: recommendationResults,
            agronomicInputs: recommendationAgronomicInputs,
          },
          geometry,
        },
      } = recommendations[0];

      const { id: recommendationID } = recommendationCropwise;
      const [properties] = geometry.properties || [];

      // Create plots array
      const plots =
        properties?.fields.map((field) => {
          const [longitude, latitude] = turf.centroid(field.geometry).geometry.coordinates;
          return {
            name: field.name,
            latitude,
            longitude,
            acreage: Number(field.declared_area.toFixed(2)),
            acreageUnitOfMeasure: 'ha',
          };
        }) || [];

      // Create recommended hybrids array
      const recommendedHybrids: { name: string; quantity: number }[] = recommendationResults
        .map((item) => {
          const field = properties?.fields.find((fieldItem) => fieldItem.id === item.FieldId);
          if (item?.SeedRankings?.length) {
            const seedName = item.SeedRankings[0].Seed?.SeedName || 'Unknown';
            const countryCode = lookup.byCountry(recommendationCropwise.country)?.iso3;
            const product = productCatalog.find((p) => p.productName === seedName);
            const defaultSeedingRate = Number(product?.defaultSeedingRate) || 0;
            const hectares = field?.declared_area || 0;
            const spatialClass = recommendationAgronomicInputs?.spatialClass ?? '';
            const bags = getBagsCount({
              product,
              hectares,
              defaultSeedingRate,
              countryList,
              countryCode,
              cropId,
              spatialClass,
            });

            return {
              name: seedName,
              quantity: bags,
            };
          } else {
            return {
              name: 'N/A',
              quantity: 0,
            };
          }
        })
        .filter((item) => item.name && item.name !== 'N/A' && item.name !== 'Unknown')
        .reduce((acc: { name: string; quantity: number }[], current) => {
          // Sum same products/seeds
          const index = acc.findIndex((i) => i.name === current.name);
          if (index < 0) {
            acc.push(current);
          } else {
            acc[index].quantity += current.quantity;
          }
          return acc;
        }, []);

      const recoHybridsNames = recommendedHybrids.map((item) => item.name);

      const getSeedName = (rs: any) => {
        if (rs.SeedName) {
          return rs.SeedName;
        }
        return rs.Seed ? rs.Seed.SeedName : '';
      };

      const nextBestHybrids = recommendationResults
        .map((item) => item.SeedRankings.map((rs, index) => index < 6 && (getSeedName(rs) || '')))
        .flat()
        .filter((seed) => seed)
        .filter((seed) => !recoHybridsNames.includes(seed))
        .filter((value, index, self) => self.indexOf(value) === index)
        .slice(0, 2);

      const updatedPhone = userContactFromAudit
        ? userContactFromAudit?.phoneNumber
        : recommendation?.phoneNumber;
      let phoneNumberBasedOnCountry = updatedPhone?.startsWith('+')
        ? updatedPhone
        : `+${updatedPhone}`;
      // Serbia specific phone number formatting
      if (phoneNumberBasedOnCountry?.startsWith('+381')) {
        phoneNumberBasedOnCountry = phoneNumberBasedOnCountry.replace('+381', '00381');
      }

      const countryCode = getCountryCode(recommendationCropwise.country);
      const firstName = getCapitalName(
        userContactFromAudit ? userContactFromAudit?.firstName : recommendation?.firstName
      );
      const lastName = getCapitalName(
        userContactFromAudit ? userContactFromAudit?.lastName : recommendation?.lastName
      );

      const payload: PreBookSeedsPayload = {
        recommendationID,
        Country: countryCode || 'RS',
        cropName:
          recommendationCropwise.recommendations[0].crop_name === CropConstants.CORN_SMALLCASE
            ? CropConstants.GRAIN_CORN
            : recommendationCropwise.recommendations[0].crop_name,
        User: {
          Email: recommendation?.email || '',
          FirstName: firstName,
          LastName: lastName,
          phone: phoneNumberBasedOnCountry || '',
        },
        plots,
        recommendedHybrids,
        nextBestHybrids,
      };

      // Make the API call to SIP via proxy service
      const response = await ProxyLayer.preBookSeeds(payload);

      // Debug: Log the entire response structure
      console.log('SIP API Full Response:', JSON.stringify(response, null, 2));
      console.log('Response id:', response?.id);
      console.log('Response recommendationId:', response?.recommendationId);

      // Check if response has the expected structure: { id: number, recommendationId: string }
      if (response && response.id) {
        // Handle success - redirect to SIP URL with the id from response
        const sipBaseUrl =
          process.env.REACT_APP_SIP_SYNGENTA_REDIRECT_URL || 'https://sipsyngenta.com/home';
        const sipUrl = `${sipBaseUrl}?id=${response.id}`;
        console.log('Attempting to redirect to:', sipUrl);

        // Open URL in new tab only
        const openResult = window.open(sipUrl, '_blank', 'noopener,noreferrer');
        console.log('Window.open result:', openResult);

        if (!openResult || openResult.closed || typeof openResult.closed === 'undefined') {
          console.error('Failed to open new window - popup may be blocked');
        }

        console.log('SIP integration completed successfully:', response);
      } else {
        // Handle failure
        console.error('SIP integration failed - Invalid response structure:', {
          expectedFormat: '{ id: number, recommendationId: string }',
          actualResponse: response,
        });
        // TODO: Show error message to user
      }
    } catch (error: any) {
      console.error('Error in SIP integration:', error);

      // Check if it's a network error or API error
      if (error?.response?.status) {
        console.error('API Error Status:', error.response.status);
        console.error('API Error Data:', error.response.data);
      } else if (error?.message) {
        console.error('Network Error:', error.message);
      }

      // TODO: Handle error - show error message to user
    } finally {
      setIsPreBooking(false);
    }
  };

  // Return null if no buttons should be displayed
  if (!shouldRenderStickyBar) {
    return null;
  }

  const displayComponent = (
    <StickyBar isLoading={isLoading} showDownloadBtn={showDownloadBtn} isDDG={isDDG}>
      {!isUkraine(country) && (isLoading || btnDisplayDelay) && isDDG ? (
        SkeletonLoader
      ) : (
        <ActionBarButtons
          t={t}
          downloadPdf={downloadPdf}
          country={recommendationCropwise?.country || ''}
          voucherParams={voucherParams}
          showDownloadBtn={showDownloadBtn}
          isDDG={isDDG}
          crop_id={
            recommendationCropwise?.recommendations?.length > 0
              ? recommendationCropwise.recommendations[0].crop_id
              : ''
          }
          onPreBookSeeds={handleSIPIntegration}
          isPreBooking={isPreBooking}
          isSIPUser={extendedAttributes?.isSIPUser || false}
          isScriptGenerated={scriptGenerated}
          ukraineStoreUrl={ukraineStoreUrl}
          isSeedsOnboarding={isSeedsOnboarding}
        />
      )}
      {gettingPDF && <StyledSpinner />}
    </StickyBar>
  );

  return displayComponent;
}
